import { AlgoliaSearchOptions } from 'algoliasearch';
import { keys, length, map, omit } from 'ramda';

import { compact } from '../../helpers/compact';
import { getClient, getAlgoliaIndexName } from './algolia-api';
import { normalizeFacets } from './normalize';

const generateFacetFilters = (
  facets: Record<string, string[]> = {}
): string[][] | void => {
  const result = Object.entries(facets).reduce<string[][]>(
    (acc, [filter, values]) => {
      if (values.length) {
        return [...acc, values.map((value) => `filters.${filter}:${value}`)];
      }
      return acc;
    },
    []
  );

  return length(result) ? result : undefined;
};

const getLocalizedIndexedClient = (lang: string, sorted: boolean): string => {
  const suffix = sorted ? '_sorted' : '';
  return `${getAlgoliaIndexName(lang)}${suffix}`;
};

type SearchResults = {
  query?: string;
  count: number;
  page: number;
  total_page: number;
  results: any[];
  facets: Record<string, SearchFacet>;
};

type SearchFacet = {
  nbResults: number;
  values: Record<string, number>;
};

type SearchConfig = Omit<
  AlgoliaSearchOptions,
  'query' | 'facetFilters' | 'hitsPerPage' | 'page'
> & {
  query?: string;
  lang: string;
  sorted?: boolean;
  facetFilters?: Record<string, string[]>;
  hitsPerPage?: number;
  page?: number;
};

export const search = async ({
  query,
  lang,
  sorted,
  facetFilters,
  hitsPerPage = 0,
  page = 1,
  ...options
}: SearchConfig): Promise<SearchResults> => {
  const indexName = getLocalizedIndexedClient(lang, sorted);

  const commonOptions = {
    ...options,
    hitsPerPage: hitsPerPage > 0 ? hitsPerPage : undefined,
    // Algolia start page index to 0. We choose an unified pagination system.
    // We start page index to 1
    page: page - 1,
  };

  const { results } = await getClient().search([
    {
      indexName,
      query,
      params: compact({
        ...commonOptions,
        facetFilters: generateFacetFilters(facetFilters),
      }),
    },

    ...map(
      (filter) => ({
        indexName,
        query,
        params: compact({
          ...commonOptions,
          facets: `filters.${filter as string}`,
          facetFilters: generateFacetFilters(
            omit([filter as string], facetFilters)
          ),
        }),
      }),
      keys(compact(facetFilters || {}))
    ),
  ]);

  return {
    query: results?.[0]?.query,
    count: results?.[0]?.nbHits || 0,
    page: results?.[0]?.page + 1,
    total_page: results?.[0]?.nbPages || 0,
    results: results?.[0]?.hits,
    facets: normalizeFacets(results),
  };
};
